import { WorldPos, WorldState } from "./world";
import { v4 as uuid } from "uuid";
import config from "../worldConfig";

const { FOOD_INCREASE_RATE } = config;
export enum ResourceType {
  FOOD = "FOOD",
  ANIMAL_PREY = "ANIMAL_PREY",
  INFLUENCE = "INFLUENCE",
}

export type ResourceNodeState = {
  id: string;
  resource: ResourceType;
  quantity: number;
  pos: WorldPos;
};

export type ResourceSpeNodeState<T extends ResourceType> = ResourceNodeState & {
  resource: T;
};

export const isResourceSpecNode =
  <T extends ResourceType>(rt: T) =>
  (e: ResourceNodeState): e is ResourceSpeNodeState<T> => {
    return e.resource === rt;
  };

export class ResourceHandler {
  static createNode = <T extends ResourceType>(
    pos: WorldPos,
    resource: T,
    quantity: number
  ): ResourceSpeNodeState<T> => {
    return {
      id: uuid(),
      resource,
      pos,
      quantity,
    };
  };

  static getGenericRandomNodes = (
    w: WorldState,
    r: ResourceType,
    presenceChance: number = 0.5,
    baseQuantity: number,
    varQuantity: number
  ): ResourceNodeState[] => {
    return Array.from(Array(w.size * w.size))
      .map((s, i) => {
        const hasResource = Math.random() > 1 - presenceChance;
        const pos: WorldPos = {
          x: i % w.size,
          y: Math.floor(i / w.size),
        };
        return hasResource
          ? ResourceHandler.createNode(
              pos,
              r,
              Math.floor(Math.random() * varQuantity + baseQuantity)
            )
          : null;
      })
      .reduce(
        (acc: ResourceNodeState[], e: ResourceNodeState | null) =>
          e ? [...acc, e] : acc,
        []
      );
  };

  static doIncreaseFood = (w: WorldState): WorldState => {
    return {
      ...w,
      resources: w.resources.map((r) => {
        if (r.resource !== ResourceType.FOOD) {
          return r;
        }

        return {
          ...r,
          quantity: Math.min(Math.floor(r.quantity * FOOD_INCREASE_RATE), 1000),
        };
      }),
    };
  };

  static getTotalResources = (
    w: WorldState,
    resource: ResourceType
  ): number => {
    return w.resources
      .filter((r) => r.resource === resource)
      .reduce((sum: number, r: ResourceNodeState) => sum + r.quantity, 0);
  };
}
